<template>
  <b-row>
    <b-col cols="12">
      <!-- Success/Error Toast Trigger -->

      <!-- Custom Story List Card -->
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">
            <feather-icon icon="BookOpenIcon" class="mr-1" />
            {{ $route.meta.title }}
          </h4>
          <b-button variant="primary" @click="$router.push('/stories/add')">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Tambah Story
          </b-button>
        </div>

        <!-- Search & Filter Row -->
        <b-row class="mb-2">
          <b-col md="4">
            <b-form-input
              v-model="searchQuery"
              placeholder="Cari judul story..."
              @keyup.enter="refreshTable"
            />
          </b-col>
          <b-col md="3">
            <b-form-select v-model="filterLang" :options="langOptions" @change="refreshTable" />
          </b-col>
          <b-col md="3">
            <b-form-select v-model="filterStatus" :options="statusOptions" @change="refreshTable" />
          </b-col>
          <b-col md="2">
            <b-button variant="outline-secondary" block @click="resetFilter">Reset</b-button>
          </b-col>
        </b-row>

        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          :busy="loading"
          striped
          hover
          responsive
          show-empty
          empty-text="Belum ada story. Klik 'Tambah Story' untuk membuat yang pertama!"
        >
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner variant="primary" />
              <p class="mt-1 text-muted">Memuat data...</p>
            </div>
          </template>

          <template #cell(no)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(image)="data">
            <b-img
              v-if="data.item.image_url"
              :src="data.item.image_url"
              thumbnail
              rounded
              style="width: 64px; height: 45px; object-fit: cover;"
              alt="thumbnail"
            />
            <div
              v-else
              class="bg-light d-flex align-items-center justify-content-center rounded"
              style="width: 64px; height: 45px;"
            >
              <feather-icon icon="ImageIcon" class="text-muted" size="18" />
            </div>
          </template>

          <template #cell(title)="data">
            <div>
              <span class="font-weight-bolder d-block">{{ data.item.title }}</span>
              <small class="text-muted">{{ formatDate(data.item.created_at) }}</small>
            </div>
          </template>

          <template #cell(lang)="data">
            <b-badge :variant="data.item.lang === 'id' ? 'light-primary' : 'light-info'">
              {{ data.item.lang === 'id' ? '🇮🇩 Indonesia' : '🇬🇧 English' }}
            </b-badge>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="data.item.status === 'active' ? 'light-success' : 'light-secondary'">
              <feather-icon :icon="data.item.status === 'active' ? 'CheckCircleIcon' : 'XCircleIcon'" size="12" class="mr-25" />
              {{ data.item.status === 'active' ? 'Aktif' : 'Draft' }}
            </b-badge>
          </template>

          <template #cell(blocks)="data">
            <div class="d-flex align-items-center flex-wrap" style="gap: 4px;">
              <b-badge v-if="getBlockCounts(data.item.data).text > 0" variant="light-secondary" class="px-2 py-1 font-weight-bold d-inline-flex align-items-center">
                <feather-icon icon="TypeIcon" size="13" class="mr-50 text-secondary" />
                {{ getBlockCounts(data.item.data).text }} Teks
              </b-badge>
              <b-badge v-if="getBlockCounts(data.item.data).chart > 0" variant="light-info" class="px-2 py-1 font-weight-bold d-inline-flex align-items-center">
                <feather-icon icon="BarChart2Icon" size="13" class="mr-50 text-info" />
                {{ getBlockCounts(data.item.data).chart }} Chart
              </b-badge>
              <b-badge v-if="getBlockCounts(data.item.data).map > 0" variant="light-success" class="px-2 py-1 font-weight-bold d-inline-flex align-items-center">
                <feather-icon icon="MapIcon" size="13" class="mr-50 text-success" />
                {{ getBlockCounts(data.item.data).map }} Peta
              </b-badge>
              <b-badge v-if="getBlockCounts(data.item.data).image > 0" variant="light-warning" class="px-2 py-1 font-weight-bold d-inline-flex align-items-center">
                <feather-icon icon="ImageIcon" size="13" class="mr-50 text-warning" />
                {{ getBlockCounts(data.item.data).image }} Gambar
              </b-badge>
              <b-badge v-if="getBlockCounts(data.item.data).infobox > 0" variant="light-primary" class="px-2 py-1 font-weight-bold d-inline-flex align-items-center">
                <feather-icon icon="BookOpenIcon" size="13" class="mr-50 text-primary" />
                {{ getBlockCounts(data.item.data).infobox }} Infobox
              </b-badge>
              <span v-if="!data.item.data || !data.item.data.blocks || data.item.data.blocks.length === 0" class="text-muted small">Kosong</span>
            </div>
          </template>

          <template #cell(aksi)="data">
            <div class="d-flex">
              <b-button
                size="sm"
                variant="warning"
                class="btn-icon mr-50"
                v-b-tooltip.hover title="Edit Story"
                @click="$router.push('/stories/' + data.item.id)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                class="btn-icon"
                v-b-tooltip.hover title="Hapus Story"
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-2">
          <span class="text-muted small">
            Menampilkan {{ items.length }} dari {{ totalRows }} story
          </span>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
            @change="onPageChange"
          >
            <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
            <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
          </b-pagination>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<style scoped>
.table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6e6b7b;
}
</style>

<script>
import {
  BRow, BCol, BCard, BTable, BButton, BPagination,
  BFormInput, BFormSelect, BBadge, BImg, BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import axios from '@axios'
import Ripple from 'vue-ripple-directive'
import { FeatherIcon } from 'vue-feather-icons'

export default {
  name: 'StoriesList',
  components: {
    BRow, BCol, BCard, BTable, BButton, BPagination,
    BFormInput, BFormSelect, BBadge, BImg, BSpinner,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      items: [],
      loading: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      searchQuery: '',
      filterLang: '',
      filterStatus: '',
      fields: [
        { key: 'no', label: '#', thStyle: 'width: 40px' },
        { key: 'image', label: 'Cover', thStyle: 'width: 80px' },
        { key: 'title', label: 'Judul Story' },
        { key: 'lang', label: 'Bahasa', thStyle: 'width: 130px' },
        { key: 'status', label: 'Status', thStyle: 'width: 110px' },
        { key: 'blocks', label: 'Konten', thStyle: 'width: 150px' },
        { key: 'aksi', label: 'Aksi', thStyle: 'width: 100px' },
      ],
      langOptions: [
        { value: '', text: 'Semua Bahasa' },
        { value: 'id', text: '🇮🇩 Indonesia' },
        { value: 'en', text: '🇬🇧 English' },
      ],
      statusOptions: [
        { value: '', text: 'Semua Status' },
        { value: 'active', text: '✅ Aktif' },
        { value: 'inactive', text: '📝 Draft' },
      ],
    }
  },
  mounted() {
    this.fetchData()
    // Show success toast if redirected from form
    if (this.$route.params.event === 'success') {
      this.$bvToast.toast(this.$route.params.text || 'Story berhasil disimpan.', {
        title: this.$route.params.title || 'Berhasil!',
        variant: 'success',
        solid: true,
        autoHideDelay: 4000,
      })
    }
  },
  methods: {
    fetchData() {
      this.loading = true
      let params = `?per_page=${this.perPage}&page=${this.currentPage}&type=story&orderby=id&sort=desc`
      if (this.searchQuery) params += `&key=${encodeURIComponent(this.searchQuery)}`
      if (this.filterLang) params += `&lang=${this.filterLang}`
      if (this.filterStatus) params += `&status=${this.filterStatus}`

      axios.get(`/content${params}`)
        .then(resp => {
          const data = resp.data.data
          this.items = data.rows || []
          this.totalRows = data.totalData || 0
          this.currentPage = data.currentPage || 1
        })
        .catch(err => {
          console.error(err)
          this.$bvToast.toast('Gagal memuat data stories.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    refreshTable() {
      this.currentPage = 1
      this.fetchData()
    },
    resetFilter() {
      this.searchQuery = ''
      this.filterLang = ''
      this.filterStatus = ''
      this.refreshTable()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    getBlockCounts(data) {
      const counts = { text: 0, chart: 0, map: 0, image: 0, infobox: 0 }
      if (!data || !data.blocks) return counts
      data.blocks.forEach(b => {
        if (counts[b.type] !== undefined) counts[b.type]++
      })
      return counts
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    confirmDelete(id) {
      this.$swal({
        title: 'Hapus Story ini?',
        text: 'Story yang dihapus tidak dapat dikembalikan.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Hapus',
        cancelButtonText: 'Batal',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          axios.delete(`/content/${id}`)
            .then(() => {
              this.$bvToast.toast('Story berhasil dihapus.', {
                title: 'Dihapus!',
                variant: 'success',
                solid: true,
              })
              this.fetchData()
            })
            .catch(err => {
              this.$bvToast.toast(err.response?.data?.message || 'Gagal menghapus story.', {
                title: 'Gagal',
                variant: 'danger',
                solid: true,
              })
            })
        }
      })
    },
  },
}
</script>
